/**
 * `schemas/catalogue.ts` — `spec.md` §6.4.
 *
 * The catalogue listing plus the two index dimensions the drawer and the Artist / Tag
 * screens are built on. `TrackSummary` and `TrackDetail` themselves live in
 * `track.ts`; this module owns the *listings*.
 */
import * as z from 'zod';
import { BooleanParam, CursorSchema, IntParam, NonEmptyText, NullableText, PageSchema, PositiveIntParam, RowId, SeedParam, TotalCount, limitParam, } from './common.js';
import { TagKind, TrackPageSchema, Year } from './track.js';
/* ────────────────────────────────────────────────────────────────────────────
 * GET /api/catalogue
 * ──────────────────────────────────────────────────────────────────────────── */
export const CATALOGUE_SORTS = ['recent', 'popular', 'oldest', 'random'];
export const CatalogueSort = z.enum(CATALOGUE_SORTS);
/**
 * The catalogue querystring, exactly the table in §6.4.
 *
 * Two cross-field rules are enforced here rather than in the route, so that both the
 * API and the web client's request builder get them for free:
 *  - `seed` is REQUIRED when `sort=random` — without it `RANDOM()` reshuffles between
 *    pages and the same track shows up three times.
 *  - `minDuration <= maxDuration`, which otherwise silently returns an empty page.
 */
export const CatalogueQuerySchema = z
    .object({
    sort: CatalogueSort.default('recent'),
    seed: SeedParam.optional(),
    limit: limitParam(1, 60, 30),
    cursor: CursorSchema.optional(),
    year: IntParam.min(1000).max(9999).optional(),
    artistId: PositiveIntParam.optional(),
    tagId: PositiveIntParam.optional(),
    minDuration: IntParam.nonnegative().optional(),
    maxDuration: IntParam.nonnegative().optional(),
    hasCover: BooleanParam.optional(),
})
    .refine((q) => q.sort !== 'random' || q.seed !== undefined, {
    error: 'seed is required when sort=random',
    path: ['seed'],
})
    .refine((q) => q.minDuration === undefined || q.maxDuration === undefined || q.minDuration <= q.maxDuration, { error: 'minDuration must not exceed maxDuration', path: ['minDuration'] });
/**
 * A drop divider. `kind: 'day'` groups by posting day, `kind: 'drop'` by a media group
 * (`tg_grouped_id`) that arrived together. `afterIndex` is the 0-based index of the
 * item the divider is drawn *after*; `-1` means "before the first item".
 */
export const CatalogueSectionSchema = z.strictObject({
    afterIndex: z.int().min(-1),
    kind: z.enum(['day', 'drop']),
    label: NonEmptyText,
    count: z.int().nonnegative(),
});
/**
 * `Page<TrackSummary> & { sections }`.
 *
 * `total` is re-declared as REQUIRED here: §6.4 states it is always included for the
 * catalogue (a real `COUNT(*)` over the filtered set, cached 60 s in-process).
 */
export const CatalogueResponseSchema = TrackPageSchema.extend({
    total: TotalCount,
    sections: z.array(CatalogueSectionSchema),
});
/* ────────────────────────────────────────────────────────────────────────────
 * GET /api/artists — the drawer index data source
 * ──────────────────────────────────────────────────────────────────────────── */
export const INDEX_SORTS = ['count', 'alpha'];
export const IndexSort = z.enum(INDEX_SORTS);
export const ArtistsQuerySchema = z.object({
    limit: limitParam(1, 200, 100),
    cursor: CursorSchema.optional(),
    sort: IndexSort.default('count'),
    /** Prefix filter, normalised server-side through `normalizeText` before matching. */
    q: z.string().min(1).max(128).optional(),
});
export const ArtistSummarySchema = z.strictObject({
    id: RowId,
    name: NonEmptyText,
    trackCount: z.int().nonnegative(),
    firstYear: Year.nullable(),
    lastYear: Year.nullable(),
});
export const ArtistsResponseSchema = PageSchema(ArtistSummarySchema).extend({
    total: TotalCount,
});
/* ────────────────────────────────────────────────────────────────────────────
 * GET /api/artists/:id
 * ──────────────────────────────────────────────────────────────────────────── */
export const ArtistStatsSchema = z.strictObject({
    trackCount: z.int().nonnegative(),
    durationS: z.int().nonnegative(),
    yearFrom: Year.nullable(),
    yearTo: Year.nullable(),
    riddimCount: z.int().nonnegative(),
});
export const ArtistSourceSchema = z.strictObject({
    label: NonEmptyText,
    url: z.url(),
});
/** The nested `tracks` page of a detail screen takes its own paging parameters. */
export const NestedTracksQuerySchema = z.object({
    limit: limitParam(1, 200, 60),
    cursor: CursorSchema.optional(),
});
/**
 * `bio` is `null` and `sources` is `[]` in v1 — there is no data source. The fields
 * exist so the Artist screen's prose block has a defined shape, and §6.4 is explicit
 * that the screen must OMIT the block when `bio === null` rather than print a
 * placeholder. `NullableText` makes `''` invalid, which is what stops a blank block
 * from ever being rendered as if it had content.
 */
export const ArtistDetailResponseSchema = z.strictObject({
    artist: ArtistSummarySchema,
    stats: ArtistStatsSchema,
    bio: NullableText,
    sources: z.array(ArtistSourceSchema),
    tracks: TrackPageSchema,
});
/* ────────────────────────────────────────────────────────────────────────────
 * GET /api/tags and GET /api/tags/:id — "same shape as artists, plus `kind`"
 * ──────────────────────────────────────────────────────────────────────────── */
export const TagsQuerySchema = z.object({
    limit: limitParam(1, 200, 100),
    cursor: CursorSchema.optional(),
    sort: IndexSort.default('count'),
    q: z.string().min(1).max(128).optional(),
    kind: TagKind.optional(),
});
export const TagSummarySchema = z.strictObject({
    id: RowId,
    kind: TagKind,
    name: NonEmptyText,
    trackCount: z.int().nonnegative(),
    firstYear: Year.nullable(),
    lastYear: Year.nullable(),
});
export const TagsResponseSchema = PageSchema(TagSummarySchema).extend({
    total: TotalCount,
});
export const TagDetailResponseSchema = z.strictObject({
    tag: TagSummarySchema,
    definition: NullableText,
    tracks: TrackPageSchema,
});
//# sourceMappingURL=catalogue.js.map